import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, X } from 'lucide-react';
import clsx from 'clsx';
import { usePlaidLink } from 'react-plaid-link';
import { useNavigate } from 'react-router-dom';
import styles from './PadVerificationMethod.module.css';
import { useModal } from '../../../hooks/modalContext';
import { useAuth } from '../../../../auth/Auth';
import { createPlaidLink, plaidCallback } from '../../../../auth/_requests';
import { showToast } from '../../../utils/showToast';
import { trackPaymentMethodAdded } from '../../../utils/analytics';
import { usePaymentOptions } from '../../../hooks/PaymentOptionsContext';
import SVGChecked from '../../../SVG/SVGRadio/SVGChecked';
import SVGUnchecked from '../../../SVG/SVGRadio/SVGUnchecked';

type VerificationMethod = 'bank' | 'manual';

const PadVerificationMethod = () => {
  const navigate = useNavigate();
  const { setModal, modalState } = useModal();
  const { auth } = useAuth();
  const { refreshPaymentOptions } = usePaymentOptions();

  const [selectedMethod, setSelectedMethod] = useState<VerificationMethod | null>(null);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isPlaidFlowActive, setIsPlaidFlowActive] = useState(false);
  const getErrorMessage = (error: unknown) => {
    if (error instanceof Error) return error.message;
    return '';
  };
  const getPlaidErrorToastMessage = (message: string) => {
    if (message.includes('user_id_is_required')) {
      return 'Unable to verify your account right now. Please sign in again and try.';
    }
    if (message.includes('user_not_found') || message.includes('unique_id_not_found')) {
      return 'We could not find your account details. Please sign in again and try.';
    }
    if (message.includes('error_generating_plaid_link')) {
      return 'Bank verification is temporarily unavailable. Please try again shortly.';
    }
    return 'Failed to verify bank account. Please try again.';
  };

  const continueLabel = useMemo(() => {
    if (loading && selectedMethod === 'bank') {
      return 'Connecting...';
    }
    return 'Continue';
  }, [loading, selectedMethod]);

  const handlePlaidSuccess = async (publicToken: string) => {
    setLoading(true);
    try {
      const response = await plaidCallback(auth?.data.token, publicToken);
      await refreshPaymentOptions();
      const accountsAdded = response?.data?.count || 0;
      showToast({
        message:
          accountsAdded > 0
            ? `Bank account${accountsAdded > 1 ? 's' : ''} added successfully!`
            : 'Bank account connected successfully!',
        type: 'success',
      });
      trackPaymentMethodAdded('plaid', { accounts_added: accountsAdded });
      const tenantBuildingId = modalState?.tenantBuildingId;
      const payPlan = modalState?.feePaymentPlan === 'direct' ? 'direct' : 'autopay';
      setModal(null);
      if (tenantBuildingId) {
        navigate('/pay-rent', {
          state: {
            buildingId: tenantBuildingId,
            initialStep: 1,
            payPlan,
            syncPayFlowFromState: true,
          },
        });
      }
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      showToast({
        message: getPlaidErrorToastMessage(message),
        type: 'error',
      });
    } finally {
      setLoading(false);
      setIsPlaidFlowActive(false);
      setLinkToken(null);
    }
  };

  const handlePlaidExit = (error: { error_message?: string } | null) => {
    setIsPlaidFlowActive(false);
    setLinkToken(null);

    if (error?.error_message) {
      showToast({
        message: error.error_message,
        type: 'error',
      });
    }
  };

  const { open, ready, error } = usePlaidLink({
    token: linkToken,
    onSuccess: handlePlaidSuccess,
    onExit: handlePlaidExit,
  });

  useEffect(() => {
    if (isPlaidFlowActive && ready) {
      open();
    }
  }, [isPlaidFlowActive, ready, open]);

  useEffect(() => {
    if (error) {
      setIsPlaidFlowActive(false);
      setLinkToken(null);
      showToast({
        message: 'Unable to open bank verification right now. Please try again.',
        type: 'error',
      });
    }
  }, [error]);

  const handleContinue = async () => {
    if (!selectedMethod || loading) return;

    if (selectedMethod === 'manual') {
      setModal('add-bank-account', {
        tenantBuildingId: modalState?.tenantBuildingId,
        payRentSetupFlow: modalState?.payRentSetupFlow,
        feePaymentPlan: modalState?.feePaymentPlan,
      });
      return;
    }

    try {
      setLoading(true);
      const response = await createPlaidLink(auth?.data.token);
      const token = response?.data?.link || null;
      if (!token) {
        throw new Error('plaid_link_missing');
      }
      setLinkToken(token);
      setIsPlaidFlowActive(true);
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      showToast({
        message: getPlaidErrorToastMessage(message),
        type: 'error',
      });
      setIsPlaidFlowActive(false);
      setLinkToken(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.main
      className={styles.main}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className={styles.headingOuter}>
        <div className='flex items-center gap-[12px]'>
          <button
            onClick={() =>
              setModal('add-payment-method', {
                tenantBuildingId: modalState?.tenantBuildingId,
                payRentSetupFlow: modalState?.payRentSetupFlow,
                feePaymentPlan: modalState?.feePaymentPlan,
              })
            }
          >
            <ChevronLeft stroke='#0E0E0B' />
          </button>
          <span>Pre authorized debit</span>
        </div>
        <X className={styles.crossIcon} size={24} onClick={() => setModal(null)} stroke='#0E0E0B' />
      </div>

      <div className={styles.subHeading}>
        Choose how you would like to verify your bank account.
        {selectedMethod === 'manual' && (
          <span className={styles.pendingText}>
            Manual verification can take up to 6 business days.
          </span>
        )}
      </div>

      <div className={styles.options}>
        <button
          type='button'
          className={clsx(styles.optionCard, selectedMethod === 'bank' && styles.optionCardActive)}
          onClick={() => setSelectedMethod('bank')}
        >
          <span className={styles.recommended}>Recommended</span>
          <div className={styles.radioOuter}>
            {selectedMethod === 'bank' ? <SVGChecked /> : <SVGUnchecked />}
          </div>
          <div className={styles.optionText}>
            <span className={styles.optionTitle}>Verify with bank</span>
            <span className={styles.optionDescription}>
              Connect instantly with Plaid, add your account faster, and enjoy a faster rent
              payment timeline.
            </span>
          </div>
        </button>

        <button
          type='button'
          className={clsx(
            styles.optionCard,
            selectedMethod === 'manual' && styles.optionCardActive
          )}
          onClick={() => setSelectedMethod('manual')}
        >
          <div className={styles.radioOuter}>
            {selectedMethod === 'manual' ? <SVGChecked /> : <SVGUnchecked />}
          </div>
          <div className={styles.optionText}>
            <span className={styles.optionTitle}>Verify manually</span>
            <span className={styles.optionDescription}>
              Enter your bank account details and complete manual verification.
            </span>
          </div>
        </button>
      </div>

      <button
        type='button'
        className={clsx(styles.continueBtn, selectedMethod && !loading && styles.continueBtnActive)}
        disabled={!selectedMethod || loading}
        onClick={handleContinue}
      >
        {continueLabel}
      </button>
    </motion.main>
  );
};

export default PadVerificationMethod;
